import type { GrindKey, SubFrequencyKey, Subscription } from '@c214/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api';
import { useAuth } from './useAuth';

export const SUBSCRIPTION_QUERY_KEY = ['subscription'] as const;

export function useSubscription() {
  const { data: user } = useAuth();
  return useQuery({
    queryKey: SUBSCRIPTION_QUERY_KEY,
    queryFn: () => apiGet<Subscription | null>('/api/subscription'),
    enabled: !!user,
  });
}

interface SubscriptionInput {
  productId: number | null;
  frequency: SubFrequencyKey;
  qty: number;
  grind: GrindKey;
}

function useInvalidatingMutation<TInput>(mutationFn: (input: TInput) => Promise<Subscription>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (subscription) => queryClient.setQueryData(SUBSCRIPTION_QUERY_KEY, subscription),
  });
}

export function useCreateSubscription() {
  return useInvalidatingMutation((input: SubscriptionInput) =>
    apiPost<Subscription>('/api/subscription', input),
  );
}

export function useUpdateSubscription() {
  return useInvalidatingMutation((input: Partial<SubscriptionInput>) =>
    apiPatch<Subscription>('/api/subscription', input),
  );
}

export function useSkipSubscription() {
  return useInvalidatingMutation(() => apiPatch<Subscription>('/api/subscription/skip'));
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<void>('/api/subscription'),
    onSuccess: () => queryClient.setQueryData(SUBSCRIPTION_QUERY_KEY, null),
  });
}
